import { useCallback, type MutableRefObject } from "react";
import { useForm as useFormHook } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  phoneCollectionSchema,
  updateUserPhone,
  type PhoneCollectionFormData,
} from "@core/features/profile";
import { usePlatformStore } from "@platform/store";
import {
  useStepFormSync,
  type StepValidationState,
} from "@platform/features/onboarding";
import type { User } from "@platform/types/platform.types";
import { PhoneCollectionForm } from "./PhoneCollectionForm";

export type Props = {
  /** Callback when phone is saved successfully */
  onSaveSuccess: () => void;
  /** Callback when user skips (optional - defaults to calling onSaveSuccess) */
  onSkip?: () => void;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Additional CSS classes for the container */
  className?: string;
};

/**
 * Container component for PhoneCollectionForm.
 * Handles mutations, store access, and form state management.
 * Used during onboarding to collect phone number from Google OAuth users.
 */
export function PhoneCollectionFormContainer({
  onSaveSuccess,
  onSkip,
  primaryButtonText = "Save & Continue",
  showActionButtons = true,
  onStepStateChange,
  submitRef,
  className,
}: Props) {
  const setUser = usePlatformStore((s) => s.setUser);

  const mutation = useMutation({
    mutationFn: async (data: PhoneCollectionFormData) => {
      return updateUserPhone({
        phoneNumber: data.phoneNumber ?? "",
        isTextNotificationsEnabled: data.isTextNotificationsEnabled ?? false,
      });
    },
    onSuccess: (data) => {
      setUser(data.user_data as unknown as User);
      onSaveSuccess();
    },
  });

  const form = useFormHook<PhoneCollectionFormData>({
    resolver: zodResolver(phoneCollectionSchema),
    mode: "all",
    defaultValues: {
      phoneNumber: "",
      confirmPhoneNumber: "",
      isTextNotificationsEnabled: false,
    },
  });

  const handleSubmit = useCallback(() => {
    return form.handleSubmit(async (data) => {
      try {
        // Only save if phone number was provided
        if (data.phoneNumber && data.phoneNumber.trim() !== "") {
          await mutation.mutateAsync(data);
        } else {
          // Empty phone number treated as skip
          onSaveSuccess();
        }
      } catch (error) {
        form.setError("root", {
          type: "manual",
          message:
            error instanceof Error
              ? error.message
              : "Failed to save phone number",
        });
      }
    })();
  }, [form, mutation, onSaveSuccess]);

  const handleSkip = useCallback(() => {
    if (onSkip) {
      onSkip();
    } else {
      onSaveSuccess();
    }
  }, [onSkip, onSaveSuccess]);

  const { isSubmitting, isValid, isDirty } = form.formState;

  // Calculate canSubmit: allow submit if phone is empty (skip scenario) or form is valid
  // Always disable during submission
  const phoneValue = form.watch("phoneNumber");
  const canSubmit = isSubmitting
    ? false
    : phoneValue
      ? isDirty && isValid
      : true;

  // Sync form state with parent when action buttons are hidden
  useStepFormSync({
    shouldSync: !showActionButtons,
    onStepStateChange,
    submitRef,
    formState: { isValid, isDirty, isSubmitting },
    onSubmit: handleSubmit,
    // Phone collection can proceed even without changes (skip scenario)
    canProceed: true,
    primaryLabel: primaryButtonText,
  });

  return (
    <div className={className}>
      <PhoneCollectionForm
        form={form}
        onSubmit={handleSubmit}
        onSkip={handleSkip}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        canSubmit={canSubmit}
        primaryButtonText={primaryButtonText}
        showActionButtons={showActionButtons}
      />
    </div>
  );
}

export default PhoneCollectionFormContainer;
